import { useState, useEffect, useRef, useMemo } from "react";
import {
  X, Search, Plus, ChevronLeft, ChevronRight, ChevronDown,
  User, Phone, MapPin, AlertTriangle, CheckCircle2, Loader2, Truck, Store,
} from "lucide-react";
import axios from "axios";
import {
  GET_MANAGER_DAILY_MENU_URL,
  GET_MANAGER_ROUTES_URL,
  SEARCH_CUSTOMERS_URL,
  CREATE_CUSTOMER_URL,
  GET_ADDRESS_BY_CODE_URL,
  CREATE_ADDRESS_URL,
  PATCH_ADDRESS_URL,
  CREATE_ORDER_URL,
  PATCH_ORDER_URL,
  GET_ROUTE_FOR_ADDRESS_URL,
  PATCH_CUSTOMER_URL,
  GET_MANAGER_PRICE_LISTS_URL,
  GET_MANAGER_ORDER_PRICES_URL,
  GET_CUSTOMER_ADDRESSES_URL,
  ORDER_CONFIG_URL,
} from "../../../features/UrlHelper";
import { DatePicker, PhoneInput, Modal, Button, QuantityInput, Select } from "../../../components";
import DeliveryLocationPicker from "../../../components/DeliveryLocationPicker/DeliveryLocationPicker";
import { PAYMENT_TYPE_OPTIONS_NEW } from "../../../components/PaymentForm/PaymentForm";
import { getDeliveryLocation } from "../../../features/orderUtils";
import { useAuth } from "../../../context/AuthContext";
import { compareMenuCodesNatural, getComposedMenus } from "../../../features/orderUtils";
import { ITEM_TYPE_META } from "../../../features/orderConstants";
import "./OrderModal.scss";

// ─── Constants ────────────────────────────────────────────────────────────────
// ─── Helpers ──────────────────────────────────────────────────────────────────
function toISODate(d) {
  const date = d instanceof Date ? d : new Date(d);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatDateSK(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`;
}

function toSlovakDateKey(value = new Date()) {
  const date = value instanceof Date ? value : new Date(value);
  const formatted = new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Europe/Bratislava",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);

  const [year, month, day] = formatted.split("-");
  return `${year}-${month}-${day}`;
}


function calcTotal(menuItems, quantities) {
  let soupQty = 0, mainQty = 0;
  let soupTotal = 0, mainTotal = 0, otherTotal = 0;

  for (const item of menuItems) {
    const qty = quantities[item.id] || 0;
    if (qty <= 0) continue;
    const price = parseFloat(item.price) || 0;
    if (item.item_type === "SOUP") {
      soupQty += qty;
      soupTotal += price * qty;
    } else if (item.item_type === "MENU") {
      mainQty += qty;
      mainTotal += price * qty;
    } else {
      otherTotal += price * qty;
    }
  }

  const pairs = Math.min(soupQty, mainQty);
  const standaloneSoups = soupQty - pairs;
  const standaloneMains = mainQty - pairs;
  // Polievky spárované s hlavným jedlom sú zadarmo; účtujú sa len samostatné polievky
  const soupAvgPrice = soupQty > 0 ? soupTotal / soupQty : 0;
  const total = mainTotal + standaloneSoups * soupAvgPrice + otherTotal;
  return { total, soupQty, mainQty, pairs, standaloneSoups, standaloneMains };
}

function buildOrderSummary(menuItems, quantities) {
  const selected = menuItems.filter(i => (quantities[i.id] || 0) > 0);
  if (selected.length === 0) return "";
  return selected
    .map(i => `${i.menu_code || i.name}×${quantities[i.id]}`)
    .join(" + ");
}

function normalizeMenuCode(value) {
  return String(value || "").trim().replace(/\s+/g, "").toUpperCase();
}

function hasAddressOrCode(address, addressCode) {
  return String(address || "").trim().length > 0 || String(addressCode || "").trim().length > 0;
}

const EMPTY_NEW_CUSTOMER = {
  name: "", surname: "", phone_number: "", email: "",
  address: "", address_code: "", default_payment_type: "",
};

const EMPTY_NEW_ADDRESS = { address: "", address_code: "", default_payment_type: "" };

const DEFAULT_PAYMENT_TYPE_OPTIONS = [
  { value: "", label: "Nenastavené" },
  ...PAYMENT_TYPE_OPTIONS_NEW,
];

// ─── OrderModal ───────────────────────────────────────────────────────────────
function OrderModal({ isOpen, onClose, defaultDate, onOrderCreated, editOrder = null, onOrderUpdated, existingOrders = [] }) {
  const { isAdmin, userRole } = useAuth();
  const canManageAddressDefaultPaymentType = ["ADMIN", "SUPERADMIN", "MANAGER"].includes(userRole);

  // Customer
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerResults, setCustomerResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [priceLists, setPriceLists] = useState([]);
  const [selectedPriceList, setSelectedPriceList] = useState("");
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [newCustomer, setNewCustomer] = useState(EMPTY_NEW_CUSTOMER);
  const [phoneDialCode, setPhoneDialCode] = useState("+421");
  const [phoneLocal, setPhoneLocal] = useState("");

  const [showNewAddress, setShowNewAddress] = useState(false);
  const [newAddress, setNewAddress] = useState(EMPTY_NEW_ADDRESS);
  const [selectedAddressDefaultPaymentType, setSelectedAddressDefaultPaymentType] = useState("");
  const [addressCodeLoading, setAddressCodeLoading] = useState(false);
  const [newCustomerAddressError, setNewCustomerAddressError] = useState(false);
  const [newAddressPairError, setNewAddressPairError] = useState(false);

  // Delivery
  const [deliveryDate, setDeliveryDate] = useState(
    defaultDate ? toISODate(defaultDate) : toISODate(new Date())
  );
  const [selectedRouteId, setSelectedRouteId] = useState("");
  // Spôsob odberu (R43) — dispečer ho má k dispozícii aj pri telefonickej
  // objednávke: zákazník mu po telefóne povie, že si príde sám.
  const [deliveryType, setDeliveryType] = useState("DELIVERY");
  // Okno webových objednávok (R12). Obsluhu **neobmedzuje** — telefonicky sa
  // da objednať kedykoľvek. Je to len upozornenie, aby dispečer vedel, že
  // zákazníkovi už strana hovorí "na dnes sa nedá".
  const [okno, setOkno] = useState(null);
  const [deliveryLocation, setDeliveryLocation] = useState(null);
  const [locationPickerOpen, setLocationPickerOpen] = useState(false);

  // Menu
  const [menuItems, setMenuItems] = useState([]);
  const [menuLoading, setMenuLoading] = useState(false);
  const [pricingLoading, setPricingLoading] = useState(false);
  // Kódy menu, pre ktoré zvolený cenník nemá cenu — v ňom sa nedajú objednať.
  // Pri STANDARD cenníku je zoznam vždy prázdny (cena sa berie z denného menu).
  const [blockedMenuCodes, setBlockedMenuCodes] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [activeCategory, setActiveCategory] = useState(null);

  // Note & routes
  const [note, setNote] = useState("");
  const [routes, setRoutes] = useState([]);

  // Customer note & saldo
  const [customerNote, setCustomerNote] = useState("");

  // Submission
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [debtConfirmOpen, setDebtConfirmOpen] = useState(false);
  // Zmena spôsobu odberu na už existujúcej objednávke (R47). Zákazník si
  // vybral jedno a dostane druhé — ide o dohodu s ním, nie o oklepnutie.
  const [sposobConfirmOpen, setSposobConfirmOpen] = useState(false);

  // Smart edit: ID of an auto-filled existing order (create mode only)
  const [autoFilledOrderId, setAutoFilledOrderId] = useState(null);
  // Delivery date of the auto-filled order — if the admin manually changes the date away
  // from this, we're no longer continuing that order but starting a brand-new one for the new day.
  const autoFilledOrderDateRef = useRef(null);

  const isEditMode = !!editOrder;

  const searchRef = useRef(null);
  const searchTimerRef = useRef(null);
  const routeDropRef = useRef(null);
  const [routeDropOpen, setRouteDropOpen] = useState(false);
  // Holds pre-filled quantities for edit mode; applied once inside loadMenu
  const initialQtyRef = useRef(null);
  // Tracks the latest loadMenu call so stale responses don't overwrite state
  const loadMenuRunRef = useRef(0);
  // Ceny položiek, ktoré už v editovanej objednávke sú (daily_menu_id → { price, ceny_id }).
  // Takéto jedlá ostávajú objednateľné aj vtedy, keď im cenník cenu nedáva.
  const orderedPricesRef = useRef({});

  // Reset when opening
  useEffect(() => {
    if (!isOpen) return;
    setCustomerSearch("");
    setCustomerResults([]);
    setSelectedCustomer(null);
    setPriceLists([]);
    setSelectedPriceList("");
    setSelectedAddressId(null);
    setNewCustomer(EMPTY_NEW_CUSTOMER);
    setPhoneDialCode("+421");
    setPhoneLocal("");
    setShowNewAddress(false);
    setNewAddress(EMPTY_NEW_ADDRESS);
    setNewAddressPairError(false);
    setSelectedAddressDefaultPaymentType("");
    setDeliveryDate(defaultDate ? toISODate(defaultDate) : toISODate(new Date()));
    setSelectedRouteId("");
    setDeliveryType("DELIVERY");
    setDeliveryLocation(null);
    setLocationPickerOpen(false);
    setQuantities({});
    setNote("");
    setCustomerNote("");
    setRouteDropOpen(false);
    setError("");
    setNewCustomerAddressError(false);
    setActiveCategory(null);
    initialQtyRef.current = null;
    loadMenuRunRef.current = 0;
    orderedPricesRef.current = {};
    setBlockedMenuCodes([]);
    setAutoFilledOrderId(null);
    autoFilledOrderDateRef.current = null;
    loadRoutes();

    if (editOrder) {
      // Pre-populate form from existing order
      // Saldo fallback: editOrder may lack customer_saldo if it was added to state after a
      // create/patch response that didn't include it. Look it up from existingOrders as backup.
      const salda =
        editOrder.customer_saldo != null
          ? editOrder.customer_saldo
          : existingOrders.find(o => o.customer_id === editOrder.customer_id && o.id !== editOrder.id)
              ?.customer_saldo ?? null;
      const syntheticCustomer = {
        id: editOrder.customer_id,
        name: editOrder.customer_name,
        surname: editOrder.customer_surname,
        phone_number: editOrder.customer_phone,
        note: editOrder.customer_note,
        actual_saldo: salda,
        kod_cennik: editOrder.kod_cennik || "",
        zlava: editOrder.zlava || 0,
        addresses: editOrder.customer_address_id
          ? [{
              id: editOrder.customer_address_id,
              address: editOrder.delivery_address || "",
              address_code: editOrder.address_code || "",
              default_payment_type: editOrder.default_payment_type || "",
              latitude: editOrder.latitude,
              longitude: editOrder.longitude,
            }]
          : [],
      };
      setSelectedCustomer(syntheticCustomer);
      setSelectedPriceList(editOrder.kod_cennik || "");
      setCustomerNote(editOrder.customer_note || "");
      setSelectedAddressId(editOrder.customer_address_id);
      loadCustomerAddresses(editOrder.customer_id, syntheticCustomer);
      setDeliveryDate(editOrder.delivery_date ? toISODate(new Date(editOrder.delivery_date)) : toISODate(new Date()));
      setSelectedRouteId(editOrder.route_id ? String(editOrder.route_id) : "");
      setDeliveryType(editOrder.delivery_type || "DELIVERY");
      setDeliveryLocation(getDeliveryLocation(editOrder));
      setNote(editOrder.dispatcher_note || "");
      // Build initial quantities — applied in loadMenu after menu items are fetched
      const initQty = {};
      const orderedPrices = {};
      (editOrder.items || []).forEach(item => {
        if (item.daily_menu_id && item.quantity > 0) {
          initQty[String(item.daily_menu_id)] = item.quantity;
          orderedPrices[String(item.daily_menu_id)] = {
            price: Number(item.price) || 0,
            ceny_id: item.ceny_id ?? null,
          };
        }
      });
      initialQtyRef.current = initQty;
      orderedPricesRef.current = orderedPrices;
    } else {
      setTimeout(() => searchRef.current?.focus(), 100);
    }
    loadPriceLists();
  }, [isOpen]);

  // Reload menu when date changes
  useEffect(() => {
    if (isOpen) loadMenu(deliveryDate);
  }, [deliveryDate, isOpen]);

  // Načíta sa raz na otvorenie modalu. Prevádzka bez webových objednávok
  // žiadne okno nemá, vtedy sa neukáže nič.
  useEffect(() => {
    if (!isOpen) return;
    axios
      .get(ORDER_CONFIG_URL)
      .then(({ data }) => setOkno(data?.zapnute ? data : null))
      .catch(() => setOkno(null));
  }, [isOpen]);

  async function loadMenu(date) {
    const run = ++loadMenuRunRef.current;
    setMenuLoading(true);
    try {
      const resp = await axios.get(GET_MANAGER_DAILY_MENU_URL(date));
      // Discard result if a newer loadMenu call has already started
      if (run !== loadMenuRunRef.current) return;
      const sortFn = (a, b) => {
        const ao = ITEM_TYPE_META[a.item_type]?.order ?? 9;
        const bo = ITEM_TYPE_META[b.item_type]?.order ?? 9;
        return ao !== bo ? ao - bo : compareMenuCodesNatural(a.menu_code, b.menu_code);
      };
      const dayItems = resp.data
        .filter(item => item.serving_date && toISODate(new Date(item.serving_date)) === date)
        .sort(sortFn);
      const permanentItems = resp.data
        .filter(item => !item.serving_date)
        .sort(sortFn);
      setMenuItems([...dayItems, ...permanentItems]);
      // Apply pre-filled quantities for edit mode (only on first load)
      if (initialQtyRef.current !== null) {
        setQuantities(initialQtyRef.current);
        initialQtyRef.current = null;
      } else {
        setQuantities({});
      }
      setActiveCategory(null);
    } catch (err) {
      if (run !== loadMenuRunRef.current) return;
      console.error(err);
    } finally {
      if (run === loadMenuRunRef.current) setMenuLoading(false);
    }
  }

  // Cenník sa vyhodnocuje naraz pre všetky kódy menu, aby sa položky aj suma menili spolu.
  useEffect(() => {
    if (!isOpen || menuItems.length === 0) return undefined;
    let cancelled = false;
    const menuCodes = [...new Set(menuItems.map(item => item.menu_code).filter(Boolean))];
    if (menuCodes.length === 0) return undefined;
    setPricingLoading(true);

    axios.get(GET_MANAGER_ORDER_PRICES_URL({
      kod_cennik: selectedPriceList,
      date: deliveryDate,
      customer_id: selectedCustomer?.id || "",
      kod_menu: menuCodes.join(","),
    }))
      .then(resp => {
        if (cancelled) return;
        const prices = resp.data?.prices || {};
        // "strict" = neštandardný cenník: kód menu bez sadzby sa v ňom nedá objednať,
        // už nepadáme späť na STANDARD cenu ani na cenu z denného menu.
        const strict = Boolean(resp.data?.strict);
        setBlockedMenuCodes(strict ? (resp.data?.missing || []).map(normalizeMenuCode) : []);
        setMenuItems(currentItems => currentItems.map(item => {
          const rate = prices[normalizeMenuCode(item.menu_code)];
          if (rate) return { ...item, price: rate.price, ceny_id: rate.ceny_id };
          // Jedlo bez sadzby, ktoré už v objednávke je, si drží pôvodnú cenu.
          const ordered = orderedPricesRef.current[String(item.id)];
          if (strict && ordered) return { ...item, price: ordered.price, ceny_id: ordered.ceny_id };
          // Inak platí cena z denného menu (STANDARD) — väzba na starú sadzbu už neplatí.
          return { ...item, ceny_id: null };
        }));
      })
      .catch(err => console.error(err))
      .finally(() => {
        if (!cancelled) setPricingLoading(false);
      });

    return () => { cancelled = true; };
  }, [isOpen, deliveryDate, selectedPriceList, selectedCustomer?.id, menuItems.length]);

  // Zmena cenníka (alebo zákazníka) môže znefunkčniť už navolené jedlo — vtedy ho
  // z objednávky odstránime, aby sa nedala uložiť položka bez ceny.
  useEffect(() => {
    if (blockedMenuCodes.length === 0) return;
    const blocked = new Set(blockedMenuCodes);
    setQuantities(current => {
      const next = { ...current };
      let changed = false;
      for (const item of menuItems) {
        const code = normalizeMenuCode(item.menu_code);
        if (!code || !blocked.has(code)) continue;
        if (orderedPricesRef.current[String(item.id)]) continue;
        if (next[item.id]) {
          delete next[item.id];
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, [blockedMenuCodes, menuItems]);

  async function loadRoutes() {
    try {
      const resp = await axios.get(GET_MANAGER_ROUTES_URL);
      setRoutes(resp.data);
    } catch (err) {
      console.error(err);
    }
  }

  async function loadPriceLists() {
    try {
      const resp = await axios.get(GET_MANAGER_PRICE_LISTS_URL);
      setPriceLists(resp.data || []);
    } catch (err) {
      console.error(err);
    }
  }

  async function loadCustomerAddresses(customerId, customerFallback) {
    try {
      const resp = await axios.get(GET_CUSTOMER_ADDRESSES_URL(customerId));
      setSelectedCustomer(prev => {
        if (prev && prev.id !== customerId) return prev;
        return {
          ...(prev || customerFallback),
          addresses: Array.isArray(resp.data) ? resp.data : [],
        };
      });
    } catch (err) {
      console.error(err);
    }
  }

  // Debounced customer search
  useEffect(() => {
    clearTimeout(searchTimerRef.current);
    if (customerSearch.length < 1) {
      setCustomerResults([]);
      return;
    }
    searchTimerRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const resp = await axios.get(SEARCH_CUSTOMERS_URL(customerSearch));
        setCustomerResults(resp.data);
      } catch (err) {
        console.error(err);
      } finally {
        setSearchLoading(false);
      }
    }, 280);
    return () => clearTimeout(searchTimerRef.current);
  }, [customerSearch]);

  // Auto-fill route from last order for this address
  useEffect(() => {
    if (!selectedAddressId) return;
    const selectedAddress = selectedCustomer?.addresses?.find(a => a.id === selectedAddressId);
    setSelectedAddressDefaultPaymentType(selectedAddress?.default_payment_type || "");

    axios
      .get(GET_ROUTE_FOR_ADDRESS_URL(selectedAddressId))
      .then(resp => {
        if (resp.data?.route_id) {
          setSelectedRouteId(String(resp.data.route_id));
        }
      })
      .catch(() => {});
  }, [selectedAddressId]);

  // Close route dropdown on outside click
  useEffect(() => {
    if (!routeDropOpen) return;
    function onClickOutside(e) {
      if (routeDropRef.current && !routeDropRef.current.contains(e.target)) {
        setRouteDropOpen(false);
      }
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [routeDropOpen]);

  // Direct address_code lookup — called on Enter key
  async function lookupAddressCode(code, setter) {
    if (!code || code.length < 2) return;
    setAddressCodeLoading(true);
    try {
      const resp = await axios.get(GET_ADDRESS_BY_CODE_URL(code));
      if (resp.data?.address) {
        setter(prev => ({ ...prev, address: resp.data.address }));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setAddressCodeLoading(false);
    }
  }

  // Select a customer from search results
  function selectCustomer(customer) {
    setSelectedCustomer(customer);
    setSelectedPriceList(customer.kod_cennik || "");
    setCustomerNote(customer.note || "");
    setCustomerSearch("");
    setCustomerResults([]);
    // Auto-select first address — its stored location (if any) becomes the starting point
    const firstAddress = customer.addresses?.length > 0 ? customer.addresses[0] : null;
    const firstAddressLocation = getDeliveryLocation(firstAddress);
    if (firstAddress) {
      setSelectedAddressId(firstAddress.id);
      setSelectedAddressDefaultPaymentType(firstAddress.default_payment_type || "");
      setDeliveryLocation(firstAddressLocation);
    } else {
      setSelectedAddressId(null);
      setSelectedAddressDefaultPaymentType("");
      setDeliveryLocation(null);
    }

    // Smart edit: in create mode, check for an existing ACCEPTED order for this customer
    if (!editOrder) {
      const match = existingOrders.find(
        o => o.customer_id === customer.id && o.status === "ACCEPTED"
      );
      if (match) {
        setAutoFilledOrderId(match.id);
        setNote(match.dispatcher_note || "");
        setSelectedRouteId(match.route_id ? String(match.route_id) : "");
        setDeliveryLocation(getDeliveryLocation(match) || firstAddressLocation);
        const matchDate = match.delivery_date ? toISODate(new Date(match.delivery_date)) : deliveryDate;
        autoFilledOrderDateRef.current = matchDate;
        const initQty = {};
        const orderedPrices = {};
        (match.items || []).forEach(item => {
          if (item.daily_menu_id && item.quantity > 0) {
            initQty[String(item.daily_menu_id)] = item.quantity;
            orderedPrices[String(item.daily_menu_id)] = {
              price: Number(item.price) || 0,
              ceny_id: item.ceny_id ?? null,
            };
          }
        });
        initialQtyRef.current = initQty;
        orderedPricesRef.current = orderedPrices;
        if (matchDate !== deliveryDate) {
          setDeliveryDate(matchDate); // useEffect will trigger loadMenu
        } else {
          loadMenu(matchDate); // same date, trigger manually
        }
      } else {
        setAutoFilledOrderId(null);
      }
    }
  }

  // Add new address to selected customer
  async function handleAddAddress(e) {
    e.preventDefault();
    if (!selectedCustomer) return;
    if (!hasAddressOrCode(newAddress.address, newAddress.address_code)) {
      setNewAddressPairError(true);
      return;
    }

    try {
      const addressPayload = { customer_id: selectedCustomer.id, ...newAddress };
      if (canManageAddressDefaultPaymentType) {
        addressPayload.default_payment_type = newAddress.default_payment_type || null;
      } else {
        delete addressPayload.default_payment_type;
      }

      const resp = await axios.post(
        CREATE_ADDRESS_URL,
        addressPayload
      );
      const updatedCustomer = {
        ...selectedCustomer,
        addresses: [...(selectedCustomer.addresses || []), resp.data],
      };
      setSelectedCustomer(updatedCustomer);
      setSelectedAddressId(resp.data.id);
      setSelectedAddressDefaultPaymentType(resp.data.default_payment_type || "");
      setDeliveryLocation(null);
      setShowNewAddress(false);
      setNewAddress(EMPTY_NEW_ADDRESS);
      setNewAddressPairError(false);
    } catch (err) {
      setError(err.response?.data?.message || "Chyba pri pridávaní adresy.");
    }
  }

  function changeQty(itemId, delta) {
    setQuantities(prev => {
      const current = prev[itemId] || 0;
      const next = Math.max(0, current + delta);
      if (next === 0) {
        const { [itemId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [itemId]: next };
    });
  }

  function setQtyDirect(itemId, val) {
    const n = parseInt(val) || 0;
    if (n <= 0) {
      setQuantities(prev => { const { [itemId]: _, ...rest } = prev; return rest; });
    } else {
      setQuantities(prev => ({ ...prev, [itemId]: n }));
    }
  }

  const todayMidnight = new Date();
  todayMidnight.setHours(0, 0, 0, 0);

  const { total, soupQty, mainQty, pairs } = calcTotal(menuItems, quantities);
  const totalItems = Object.values(quantities).reduce((a, b) => a + b, 0);
  const denMimoOkna = okno
    ? (okno.dni || []).find((d) => d.datum === deliveryDate && !d.objednatelny)
    : null;

  const selectedRoute = routes.find(r => String(r.id) === String(selectedRouteId)) || null;

  // Trasa výdaja na prevádzke (R53). Server stráži, že odber ide práve na ňu
  // a donáška nie (R58) — tu sa to len predvolí, aby dispečer nemusel hádať.
  const vydajnaTrasa = routes.find(r => r.interny_vydaj) || null;
  const jeOdber = deliveryType === "PICKUP";
  const ponukaneTrasy = jeOdber
    ? routes.filter(r => r.interny_vydaj)
    : routes.filter(r => !r.interny_vydaj);

  /**
   * Prepnutie spôsobu rovno dorovná trasu: pri odbere na výdajnú, pri donáške
   * sa výdajná zahodí. Bez toho by dispečer uložil kombináciu, ktorú server
   * odmietne, a hládal by prečo.
   */
  const zmenSposob = (novy) => {
    setDeliveryType(novy);
    if (novy === "PICKUP") {
      setSelectedRouteId(vydajnaTrasa ? String(vydajnaTrasa.id) : "");
    } else if (selectedRoute?.interny_vydaj) {
      setSelectedRouteId("");
    }
  };
  const priceListOptions = [
    { value: "", label: "Štandardný cenník" },
    ...priceLists.filter(priceList => priceList.kod !== "STANDARD").map(priceList => ({
      value: priceList.kod,
      label: priceList.popis ? `${priceList.kod} – ${priceList.popis}` : priceList.kod,
    })),
  ];
  const composedMenus = useMemo(() => {
    const items = menuItems.map(item => ({ ...item, quantity: quantities[item.id] || 0 }));
    return getComposedMenus(items);
  }, [menuItems, quantities]);

  let warning = "";
  if (soupQty > 0 && mainQty === 0) warning = `${soupQty}× polievka bez párového jedla`;
  else if (mainQty > 0 && soupQty === 0) warning = `${mainQty}× jedlo bez polievky`;
  else if (soupQty !== mainQty && soupQty > 0 && mainQty > 0) {
    const diff = Math.abs(soupQty - mainQty);
    warning = soupQty > mainQty
      ? `${diff}× polievka bez párového jedla`
      : `${diff}× jedlo bez polievky`;
  }

  const customerHasDebt = !isEditMode && selectedCustomer && (selectedCustomer.actual_saldo || 0) < 0;
  const isNewCustomer = !isEditMode && !selectedCustomer;
  const hasNewCustomerAddressPair = hasAddressOrCode(newCustomer.address, newCustomer.address_code);
  const isEditLockedByDate = isEditMode && editOrder && editOrder.delivery_date
    ? toSlovakDateKey(new Date(editOrder.delivery_date)) !== toSlovakDateKey(new Date())
    : false;
  const canSubmit = (
    isNewCustomer
      ? hasNewCustomerAddressPair && totalItems > 0
      // Odber na prevadzke moze byt bez adresy (zakaznik si ju pri registracii
      // nezadal), takze jeho upravu adresa neblokuje.
      : selectedCustomer
        && (selectedAddressId || (isEditMode && deliveryType === "PICKUP" && !editOrder?.customer_address_id))
        && (totalItems > 0 || customerHasDebt)
  ) && !submitting && !pricingLoading && !isEditLockedByDate;

  async function submitOrder() {
    if (isEditMode && isEditLockedByDate) {
      setError("Objednávku je možné upravovať iba pre aktuálny deň.");
      return;
    }

    setSubmitting(true);
    setError("");

    const items = Object.entries(quantities)
      .filter(([, qty]) => qty > 0)
      .map(([id, qty]) => {
        const menuItem = menuItems.find(m => String(m.id) === id);
        return {
          daily_menu_id: parseInt(id),
          quantity: qty,
          price: parseFloat(menuItem?.price) || 0,
          ceny_id: menuItem?.ceny_id || null,
        };
      });

    const updateId = editOrder?.id || autoFilledOrderId;

    try {
      let customerId = selectedCustomer?.id;
      let addressId = selectedAddressId;

      // Nový zákazník – najprv ho vytvoríme
      if (!selectedCustomer) {
        const custPayload = { ...newCustomer };
        if (!phoneLocal.trim()) custPayload.phone_number = null;
        if (customerNote) custPayload.note = customerNote;
        custPayload.kod_cennik = selectedPriceList || null;
        if (canManageAddressDefaultPaymentType) {
          custPayload.default_payment_type = newCustomer.default_payment_type || null;
        } else {
          delete custPayload.default_payment_type;
        }
        const custResp = await axios.post(CREATE_CUSTOMER_URL, custPayload);
        customerId = custResp.data.id;
        addressId = custResp.data.addresses?.[0]?.id || null;
      } else if (
        selectedPriceList !== (selectedCustomer.kod_cennik || "") ||
        (!isEditMode && customerNote !== (selectedCustomer.note || ""))
      ) {
        // Existujúci zákazník – cenník ulož vždy, poznámku iba v režime novej objednávky.
        await axios.patch(
          PATCH_CUSTOMER_URL(selectedCustomer.id),
          {
            note: isEditMode ? selectedCustomer.note : customerNote,
            kod_cennik: selectedPriceList || null,
          }
        );
      }

      if (canManageAddressDefaultPaymentType && selectedCustomer && addressId) {
        const selectedAddress = selectedCustomer.addresses?.find(a => a.id === addressId);
        const originalDefaultPaymentType = selectedAddress?.default_payment_type || "";
        const nextDefaultPaymentType = selectedAddressDefaultPaymentType || "";

        if (originalDefaultPaymentType !== nextDefaultPaymentType) {
          await axios.patch(
            PATCH_ADDRESS_URL(addressId),
            { default_payment_type: nextDefaultPaymentType || null }
          );
        }
      }

      // Presná poloha doručenia patrí adrese (customer_addresses), nie objednávke —
      // zdieľajú si ju všetky objednávky na túto adresu.
      if (addressId) {
        await axios.patch(
          PATCH_ADDRESS_URL(addressId),
          {
            latitude: deliveryLocation?.lat ?? null,
            longitude: deliveryLocation?.lng ?? null,
          }
        );
      }

      if (updateId) {
        const resp = await axios.patch(
          PATCH_ORDER_URL(updateId),
          {
            // Bez adresy (odber na prevadzke) sa neposiela -- prazdna by znamenala "zmen na nic".
            customer_address_id: addressId || undefined,
            delivery_date: deliveryDate,
            route_id: selectedRouteId || null,
            delivery_type: deliveryType,
            dispatcher_note: note || null,
            total_price: parseFloat(total.toFixed(2)),
            items,
            kod_cennik: selectedPriceList || "STANDARD",
            // Verzia zo snapshotu, s ktorym sa modal otvoril. Ked ju medzitym
            // zmenil iny dispecer, backend vrati 409 a uprava sa NEULOZI --
            // inak by toto ulozenie ticho zahodilo celu jeho pracu, lebo
            // patchOrder nahradza cely zoznam poloziek.
            expected_version: editOrder?.version,
          }
        );
        onOrderUpdated?.(resp.data);
      } else {
        const resp = await axios.post(
          CREATE_ORDER_URL,
          {
            customer_id: customerId,
            customer_address_id: addressId,
            delivery_date: deliveryDate,
            route_id: selectedRouteId || null,
            delivery_type: deliveryType,
            dispatcher_note: note || null,
            total_price: parseFloat(total.toFixed(2)),
            items,
            kod_cennik: selectedPriceList || "STANDARD",
          }
        );
        onOrderCreated?.(resp.data);
      }
    } catch (err) {
      // 409 = konflikt verzii. Backend posiela zrozumitelnu slovensku hlasku,
      // takze staci ju ukazat. Modal zostava otvoreny aj s rozpracovanymi
      // zmenami -- dispecer o svoju pracu nepride a moze sa rozhodnut sam.
      setError(err.response?.data?.message || "Chyba pri ukladaní objednávky.");
    } finally {
      setSubmitting(false);
    }
  }

  function handleSubmit() {
    if (isNewCustomer && !hasNewCustomerAddressPair) {
      setNewCustomerAddressError(true);
    }

    if (!canSubmit) return;
    if (totalItems === 0 && customerHasDebt) {
      setDebtConfirmOpen(true);
      return;
    }
    // Spôsob odberu sa na existujúcej objednávke mení len po dohode so
    // zákazníkom (R47) — a zákazník o tom dostane mail, nech už sa dohodli
    // akokoľvek.
    if (isEditMode && editOrder && (editOrder.delivery_type || "DELIVERY") !== deliveryType) {
      setSposobConfirmOpen(true);
      return;
    }
    submitOrder();
  }

  const mouseDownOnOverlay = useRef(false);

  if (!isOpen) return null;

  const groupedMenu = Object.entries(ITEM_TYPE_META)
    .sort(([, a], [, b]) => a.order - b.order)
    .map(([type, meta]) => ({
      type,
      meta,
      items: menuItems.filter(i => i.item_type === type),
    }))
    .filter(g => g.items.length > 0);

  const effectiveCategory = activeCategory ?? groupedMenu[0]?.type;

  const blockedCodeSet = new Set(blockedMenuCodes);
  const activePriceListLabel = selectedPriceList || "STANDARD";

  const summary = buildOrderSummary(menuItems, quantities);

  return (
    <>
    <div
      className="omodal__overlay"
      onMouseDown={(e) => { mouseDownOnOverlay.current = e.target === e.currentTarget; }}
      onMouseUp={(e) => { if (mouseDownOnOverlay.current && e.target === e.currentTarget) onClose(); mouseDownOnOverlay.current = false; }}
    >
      <div className="omodal">
        {/* ── Header ── */}
        <div className="omodal__header">
          <h2 className="omodal__title">{editOrder ? "Upraviť objednávku" : "Nová objednávka"}</h2>
          <button className="omodal__close" onClick={onClose} aria-label="Zavrieť">
            <X size={20} />
          </button>
        </div>

        {/* ── 3-zone body ── */}
        <div className="omodal__body">
          <div className="omodal__zones">

            {/* ══ ZÓNA 1: ZÁKAZNÍK ══════════════════════════════════════════ */}
            <div className="omodal__zone omodal__zone--customer">
              <div className="omodal__zone-title">
                <span className="omodal__num">1</span>
                Zákazník
              </div>

              {/* ─── Hľadať existujúceho zákazníka ─── */}
              {!isEditMode && (
                <>
                  <div className="omodal__search-wrap">
                    <Search size={16} className="omodal__search-icon" />
                    <input
                      ref={searchRef}
                      className="omodal__search-input"
                      placeholder="Meno, telefón, adresa…"
                      value={customerSearch}
                      onChange={e => setCustomerSearch(e.target.value)}
                      autoComplete="off"
                    />
                    {searchLoading && <Loader2 size={15} className="omodal__search-spin" />}
                    {customerSearch && (
                      <button className="omodal__search-clear" onClick={() => setCustomerSearch("")}>
                        <X size={14} />
                      </button>
                    )}
                  </div>

                  {customerResults.length > 0 && (
                    <div className="omodal__results">
                      {customerResults.map(c => (
                        <button
                          key={c.id}
                          className="omodal__result"
                          onClick={() => selectCustomer(c)}
                        >
                          <div className="omodal__result-name">
                            <User size={13} />
                            {[c.name, c.surname].filter(Boolean).join(" ") || "—"}
                          </div>
                          {c.phone_number && (
                            <div className="omodal__result-row">
                              <Phone size={11} /> {c.phone_number}
                            </div>
                          )}
                          {c.addresses?.slice(0, 2).map(a => (
                            <div key={a.id} className="omodal__result-row">
                              <MapPin size={11} />
                              {a.address_code && <strong>{a.address_code}</strong>}
                              {a.address_code && " — "}
                              {a.address}
                            </div>
                          ))}
                          {c.actual_saldo < 0 && (
                            <div className="omodal__result-debt">
                              Dlh: {Math.abs(c.actual_saldo).toFixed(2)} €
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}

                  {customerSearch.length > 0 && customerResults.length === 0 && !searchLoading && (
                    <p className="omodal__no-results">Žiadny zákazník nenájdený</p>
                  )}
                </>
              )}

              {/* ─── Karta vybratého existujúceho zákazníka ─── */}
              {selectedCustomer && (
                <div className="omodal__customer-selected">
                  <div className="omodal__customer-header">
                    <div className="omodal__customer-info">
                      <span className="omodal__customer-name">
                        {[selectedCustomer.name, selectedCustomer.surname].filter(Boolean).join(" ") || "—"}
                      </span>
                      {selectedCustomer.phone_number && (
                        <span className="omodal__customer-phone">
                          <Phone size={12} /> {selectedCustomer.phone_number}
                        </span>
                      )}
                    </div>
                    {!isEditMode && (
                      <button className="omodal__change-btn" onClick={() => {
                        setSelectedCustomer(null);
                        setSelectedPriceList("");
                        setSelectedAddressId(null);
                        setCustomerNote("");
                        setNewCustomer(EMPTY_NEW_CUSTOMER);
                        setPhoneDialCode("+421");
                        setPhoneLocal("");
                        if (autoFilledOrderId) {
                          setAutoFilledOrderId(null);
                          autoFilledOrderDateRef.current = null;
                          setQuantities({});
                          setNote("");
                          setSelectedRouteId("");
                        }
                        setTimeout(() => searchRef.current?.focus(), 50);
                      }}>
                        Zmeniť
                      </button>
                    )}
                  </div>

                  {/* Adresy */}
                  <div className="omodal__addresses">
                    <div className="omodal__addr-label">Adresa doručenia:</div>
                    {selectedCustomer.addresses?.length === 0 && (
                      <p className="omodal__no-results">Zákazník nemá žiadnu adresu</p>
                    )}
                    <div className="omodal__addr-list">
                      {selectedCustomer.addresses?.map(addr => (
                        <label
                          key={addr.id}
                          className={`omodal__addr-item${selectedAddressId === addr.id ? " omodal__addr-item--active" : ""}`}
                        >
                          <input
                            type="radio"
                            name="address"
                            value={addr.id}
                            checked={selectedAddressId === addr.id}
                            onChange={() => {
                              setSelectedAddressId(addr.id);
                              setDeliveryLocation(getDeliveryLocation(addr));
                            }}
                            className="omodal__addr-radio"
                          />
                          <div className="omodal__addr-content">
                            {addr.address_code && (
                              <span className="omodal__addr-code">{addr.address_code}</span>
                            )}
                            <span className="omodal__addr-text">{addr.address}</span>
                          </div>
                        </label>
                      ))}
                    </div>

                    <button
                      className="omodal__new-btn omodal__new-btn--sm omodal__new-btn--address"
                      onClick={() => {
                        setShowNewAddress(v => !v);
                        setNewAddressPairError(false);
                      }}
                    >
                      <Plus size={12} />
                      {showNewAddress ? "Zrušiť" : "Iná adresa"}
                    </button>

                    {selectedAddressId && (
                      <div className="omodal__payment-price-grid">
                        {canManageAddressDefaultPaymentType && (
                          <Select
                            label="Predvolený typ platby"
                            options={DEFAULT_PAYMENT_TYPE_OPTIONS}
                            value={selectedAddressDefaultPaymentType}
                            onChange={(value) => setSelectedAddressDefaultPaymentType(value || "")}
                            placeholder="Nenastavené"
                          />
                        )}
                        <Select
                          label="Cenník"
                          options={priceListOptions}
                          value={selectedPriceList}
                          onChange={(value) => setSelectedPriceList(value || "")}
                          placeholder="Štandardný cenník"
                        />
                      </div>
                    )}

                    {showNewAddress && (
                      <form className="omodal__new-form" onSubmit={handleAddAddress}>
                        <div className="omodal__row2">
                          <div className="omodal__field">
                            <label className="omodal__label">
                              Kód adresy
                              {addressCodeLoading && <Loader2 size={11} className="omodal__spin" />}
                            </label>
                            <input
                              className={`omodal__input omodal__input--code${newAddressPairError ? " omodal__input--invalid" : ""}`}
                              value={newAddress.address_code}
                              maxLength={5}
                              onChange={e => {
                                const v = e.target.value.toUpperCase();
                                setNewAddressPairError(false);
                                setNewAddress(p => ({ ...p, address_code: v }));
                              }}
                              onKeyDown={e => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  lookupAddressCode(newAddress.address_code, setNewAddress);
                                }
                              }}
                              placeholder="MAG"
                            />
                          </div>
                          <div className="omodal__field" style={{ flex: 2 }}>
                            <label className="omodal__label">Adresa</label>
                            <input
                              className={`omodal__input${newAddressPairError ? " omodal__input--invalid" : ""}`}
                              value={newAddress.address}
                              onChange={e => {
                                setNewAddressPairError(false);
                                setNewAddress(p => ({ ...p, address: e.target.value }));
                              }}
                              placeholder="Námestie SNP 1"
                            />
                          </div>
                        </div>
                        {newAddressPairError && (
                          <p className="omodal__field-error">Vyplňte kód adresy alebo adresu.</p>
                        )}
                        {canManageAddressDefaultPaymentType && (
                          <div className="omodal__field">
                            <Select
                              label="Predvolený typ platby"
                              options={DEFAULT_PAYMENT_TYPE_OPTIONS}
                              value={newAddress.default_payment_type || ""}
                              onChange={(value) => setNewAddress(p => ({ ...p, default_payment_type: value || "" }))}
                              placeholder="Nenastavené"
                            />
                          </div>
                        )}
                        <button className="omodal__submit-secondary" type="submit">
                          <Plus size={14} /> Použiť adresu
                        </button>
                      </form>
                    )}
                  </div>
                </div>
              )}

              {/* ─── Polia nového zákazníka (vždy zobrazené) ─── */}
              {!selectedCustomer && (
                <>
                  <div className="omodal__row2">
                    <div className="omodal__field">
                      <label className="omodal__label">Priezvisko</label>
                      <input
                        className="omodal__input"
                        value={newCustomer.surname}
                        onChange={e => setNewCustomer(p => ({ ...p, surname: e.target.value }))}
                        placeholder="Novák"
                      />
                    </div>
                    <div className="omodal__field">
                      <label className="omodal__label">Meno</label>
                      <input
                        className="omodal__input"
                        value={newCustomer.name}
                        onChange={e => setNewCustomer(p => ({ ...p, name: e.target.value }))}
                        placeholder="Ján"
                      />
                    </div>
                  </div>
                  <div className="omodal__row2">
                    <div className="omodal__field">
                      <label className="omodal__label">Telefón</label>
                      <PhoneInput
                        value={phoneLocal}
                        prefix={phoneDialCode}
                        onChange={local => {
                          setPhoneLocal(local);
                          setNewCustomer(p => ({ ...p, phone_number: phoneDialCode + local }));
                        }}
                        onPrefixChange={dial => {
                          setPhoneDialCode(dial);
                          setNewCustomer(p => ({ ...p, phone_number: dial + phoneLocal }));
                        }}
                      />
                    </div>
                    <div className="omodal__field">
                      <label className="omodal__label">Email (nepovinný)</label>
                      <input
                        className="omodal__input"
                        type="email"
                        value={newCustomer.email}
                        onChange={e => setNewCustomer(p => ({ ...p, email: e.target.value }))}
                        placeholder="jan@example.com"
                      />
                    </div>
                  </div>
                  <div className="omodal__row2">
                    <div className="omodal__field">
                      <label className="omodal__label">
                        Kód adresy
                        {addressCodeLoading && <Loader2 size={11} className="omodal__spin" />}
                      </label>
                      <input
                        className={`omodal__input omodal__input--code${newCustomerAddressError ? " omodal__input--invalid" : ""}`}
                        value={newCustomer.address_code}
                        maxLength={5}
                        onChange={e => {
                          const v = e.target.value.toUpperCase();
                          setNewCustomerAddressError(false);
                          setNewCustomer(p => ({ ...p, address_code: v }));
                        }}
                        onKeyDown={e => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            lookupAddressCode(newCustomer.address_code, setNewCustomer);
                          }
                        }}
                        placeholder="MAG"
                      />
                    </div>
                    <div className="omodal__field" style={{ flex: 2 }}>
                      <label className="omodal__label">Adresa</label>
                      <input
                        className={`omodal__input${newCustomerAddressError ? " omodal__input--invalid" : ""}`}
                        value={newCustomer.address}
                        onChange={e => {
                          setNewCustomerAddressError(false);
                          setNewCustomer(p => ({ ...p, address: e.target.value }));
                        }}
                        placeholder="Námestie SNP 1, Bratislava"
                      />
                    </div>
                  </div>
                  {newCustomerAddressError && (
                    <p className="omodal__field-error">Vyplňte kód adresy alebo adresu.</p>
                  )}
                  <div className="omodal__payment-price-grid">
                    {canManageAddressDefaultPaymentType && (
                      <Select
                        label="Predvolený typ platby"
                        options={DEFAULT_PAYMENT_TYPE_OPTIONS}
                        value={newCustomer.default_payment_type || ""}
                        onChange={(value) => setNewCustomer(p => ({ ...p, default_payment_type: value || "" }))}
                        placeholder="Nenastavené"
                      />
                    )}
                    <Select
                      label="Cenník"
                      options={priceListOptions}
                      value={selectedPriceList}
                      onChange={(value) => setSelectedPriceList(value || "")}
                      placeholder="Štandardný cenník"
                    />
                  </div>
                </>
              )}

              {/* ── Poznámka k zákazníkovi ── */}
              <div className="omodal__cust-note">
                <label className="omodal__label">Poznámka k zákazníkovi</label>
                <textarea
                  className="omodal__note"
                  rows={3}
                  placeholder="Interná poznámka k zákazníkovi…"
                  value={customerNote}
                  onChange={e => setCustomerNote(e.target.value)}
                  disabled={isEditMode}
                />

              </div>

              {/* ── Saldo (len existujúci zákazník s nenulový saldom) ── */}
              {selectedCustomer && Number(selectedCustomer.actual_saldo || 0) !== 0 && (
                <div className="omodal__saldo">
                  <span className="omodal__saldo-label">Saldo:</span>
                  <span className={`omodal__saldo-val${(selectedCustomer.actual_saldo || 0) < 0 ? " omodal__saldo-val--negative" : ""}`}>
                    {Number(selectedCustomer.actual_saldo || 0).toFixed(2)} €
                  </span>
                </div>
              )}
            </div>

            {/* ══ ZÓNA 2: POLOŽKY ═══════════════════════════════════════════ */}
            <div className="omodal__zone omodal__zone--items">
              <div className="omodal__zone-title">
                <span className="omodal__num">2</span>
                Jedlá
              </div>

              {menuLoading ? (
                <div className="omodal__menu-loading">
                  <Loader2 size={18} className="omodal__spin" />
                  <span>Načítavam menu…</span>
                </div>
              ) : menuItems.length === 0 ? (
                <p className="omodal__no-results">Pre tento deň nie je nastavené menu.</p>
              ) : (
                <>
                  <div className="omodal__category-tabs">
                    {groupedMenu.map(({ type, meta }) => (
                      <button
                        key={type}
                        type="button"
                        className={`omodal__category-tab${effectiveCategory === type ? " omodal__category-tab--active" : ""}`}
                        onClick={() => setActiveCategory(type)}
                      >
                        <meta.Icon size={14} />
                        <span>{meta.label}</span>
                      </button>
                    ))}
                  </div>
                  <div className="omodal__menu-list">
                    {(groupedMenu.find(g => g.type === effectiveCategory)?.items ?? []).map(item => {
                        const qty = quantities[item.id] || 0;
                        const isUnavail = Boolean(item.unavailable) && (!item.unavailable_until || new Date(item.unavailable_until) > new Date());
                        // Jedlo, ktoré zvolený cenník neocenil. Položky, ktoré už v editovanej
                        // objednávke sú, ostávajú objednateľné s pôvodnou cenou.
                        const menuCode = normalizeMenuCode(item.menu_code);
                        const isUnpriced = Boolean(menuCode)
                          && blockedCodeSet.has(menuCode)
                          && !orderedPricesRef.current[String(item.id)];
                        return (
                          <div
                            key={item.id}
                            className={`omodal__menu-item${qty > 0 ? " omodal__menu-item--selected" : ""}${isUnavail ? " omodal__menu-item--unavail" : ""}${isUnpriced && !isUnavail ? " omodal__menu-item--unpriced" : ""}`}
                          >
                            <div className="omodal__item-info">
                              {item.menu_code && (
                                <span className="omodal__item-code">{item.menu_code}</span>
                              )}
                              <div className="omodal__item-text">
                                <span className="omodal__item-name">{item.name}</span>
                                {isUnavail && (
                                  <span className="omodal__item-unavail">Nedostupné</span>
                                )}
                                {isUnpriced && !isUnavail && (
                                  <span className="omodal__item-unpriced">
                                    Nie je v cenníku {activePriceListLabel}
                                  </span>
                                )}
                                {item.description && !isUnavail && !isUnpriced && (
                                  <span className="omodal__item-desc">{item.description}</span>
                                )}
                              </div>
                            </div>
                            <div className="omodal__item-right">
                              <span className="omodal__item-price">
                                {Number(item.price || 0).toFixed(2)} €
                              </span>
                              <QuantityInput
                                value={qty || 0}
                                onChange={n => setQtyDirect(item.id, n)}
                                disabled={isUnavail || isUnpriced}
                              />
                            </div>
                          </div>
                        );
                    })}
                  </div>
                </>
              )}

              {/* Order note */}
              <div className="omodal__order-note">
                <label className="omodal__label">Poznámka k objednávke</label>
                <textarea
                  className="omodal__note"
                  rows={2}
                  placeholder="Bez rezancov, namiesto zemiakov ryža…"
                  value={note}
                  onChange={e => setNote(e.target.value)}
                />
              </div>

              {warning && (
                <div className="omodal__warning">
                  <AlertTriangle size={14} /> {warning}
                </div>
              )}
            </div>

            {/* ══ ZÓNA 3: SUMÁR ═════════════════════════════════════════════ */}
            <div className="omodal__zone omodal__zone--summary">
              <div className="omodal__zone-title">
                <span className="omodal__num">3</span>
                Doručenie &amp; Sumár
              </div>

              {/* Date + Route – grouped, no stretching */}
              <div className="omodal__delivery-fields">
                {/* Date */}
                <div className="omodal__field">
                  <DatePicker
                    label="Dátum doručenia"
                    value={deliveryDate}
                    minDate={todayMidnight}
                    onChange={d => {
                      const nextDate = d ? toISODate(d) : toISODate(new Date());
                      // Smart-edit only continues the matched pending order for its own date.
                      // A manual date change means the admin wants a brand-new order for that
                      // day — don't keep patching the old one.
                      if (autoFilledOrderId && nextDate !== autoFilledOrderDateRef.current) {
                        setAutoFilledOrderId(null);
                        autoFilledOrderDateRef.current = null;
                      }
                      setDeliveryDate(nextDate);
                    }}
                  />
                </div>

                {/* Okno objednávok (R12) — len informácia, nič neblokuje. */}
                {denMimoOkna && (
                  <div className="omodal__mimo-okna">
                    <AlertTriangle size={15} aria-hidden="true" />
                    <span>
                      Zákazník si na tento deň cez web už neobjedná
                      {denMimoOkna.dovod ? ` (${denMimoOkna.dovod.toLowerCase()})` : ""}.
                      Telefonicky objednávku prijať môžete.
                    </span>
                  </div>
                )}

                {/* Spôsob odberu (R43) */}
                <div className="omodal__field">
                  <label className="omodal__label">Spôsob</label>
                  <div className="omodal__sposob">
                    <button
                      type="button"
                      className={`omodal__sposob-btn${!jeOdber ? " omodal__sposob-btn--active" : ""}`}
                      onClick={() => zmenSposob("DELIVERY")}
                    >
                      <Truck size={14} /> Donáška
                    </button>
                    <button
                      type="button"
                      className={`omodal__sposob-btn${jeOdber ? " omodal__sposob-btn--active" : ""}`}
                      onClick={() => zmenSposob("PICKUP")}
                      disabled={!vydajnaTrasa}
                      title={vydajnaTrasa ? undefined : "Prevádzka nemá trasu výdaja na prevádzke"}
                    >
                      <Store size={14} /> Na prevádzke
                    </button>
                  </div>
                </div>

                {/* Route */}
                <div className="omodal__field omodal__route-wrap" ref={routeDropRef}>
                  <label className="omodal__label">Trasa</label>
                  <button
                    type="button"
                    className="omodal__route-trigger"
                    onClick={() => setRouteDropOpen(v => !v)}
                  >
                    {selectedRoute ? (
                      <>
                        <span className="omodal__route-dot" style={{ background: selectedRoute.color_code }} />
                        <span className="omodal__route-trigger-name">{selectedRoute.name}</span>
                      </>
                    ) : (
                      <span className="omodal__route-placeholder">— bez trasy —</span>
                    )}
                    <ChevronDown
                      size={14}
                      className={`omodal__route-chevron${routeDropOpen ? " omodal__route-chevron--open" : ""}`}
                    />
                  </button>

                  {routeDropOpen && (
                    <div className="omodal__route-dropdown">
                      <button
                        type="button"
                        className={`omodal__route-option${!selectedRouteId ? " omodal__route-option--active" : ""}`}
                        onClick={() => { setSelectedRouteId(""); setRouteDropOpen(false); }}
                      >
                        <span className="omodal__route-dot omodal__route-dot--empty" />
                        <span>— bez trasy —</span>
                      </button>
                      {ponukaneTrasy.map(r => (
                        <button
                          key={r.id}
                          type="button"
                          className={`omodal__route-option${String(selectedRouteId) === String(r.id) ? " omodal__route-option--active" : ""}`}
                          onClick={() => { setSelectedRouteId(r.id); setRouteDropOpen(false); }}
                        >
                          <span className="omodal__route-dot" style={{ background: r.color_code }} />
                          <span>{r.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Items summary */}
              {totalItems > 0 && (
                <div className="omodal__items-summary">
                  {menuItems
                    .filter(i => (quantities[i.id] || 0) > 0)
                    .map(i => (
                      <span key={i.id} className="omodal__items-summary-chip">
                        <span className="omodal__items-summary-qty">{quantities[i.id]}x</span>
                        <strong className="omodal__items-summary-code" title={i.name}>
                          {i.menu_code || i.name}
                        </strong>
                      </span>
                    ))}
                </div>
              )}

              {/* Zložené menu */}
              {composedMenus.length > 0 && (
                <div className="omodal__composed-menus">
                  <div className="omodal__composed-menus-title">Zložené menu</div>
                  <div className="omodal__composed-menus-list">
                    {composedMenus.map(({ label, count }) => (
                      <span key={label} className="omodal__composed-menu-badge">
                        <span className="omodal__composed-menu-count">{count}x</span>
                        <strong className="omodal__composed-menu-label">{label}</strong>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Summary table + submit — pinned to bottom */}
              <div className="omodal__sum-footer">
              {/* Summary table */}
              <div className="omodal__sum-table">
                <div className="omodal__sum-row">
                  <span className="omodal__sum-row-label">Zákazník</span>
                  <span className="omodal__sum-row-val">
                    {selectedCustomer
                      ? [selectedCustomer.name, selectedCustomer.surname].filter(Boolean).join(" ") || "—"
                      : (newCustomer.surname || newCustomer.name)
                        ? [newCustomer.name, newCustomer.surname].filter(Boolean).join(" ")
                        : <span className="omodal__sum-empty">— nevybraný —</span>}
                  </span>
                </div>
                <div className="omodal__sum-row">
                  <span className="omodal__sum-row-label">Adresa</span>
                  <span className="omodal__sum-row-val">
                    {selectedCustomer && selectedAddressId
                      ? (() => {
                          const addr = selectedCustomer.addresses?.find(a => a.id === selectedAddressId);
                          return addr
                            ? [addr.address_code, addr.address].filter(Boolean).join(" — ")
                            : "—";
                        })()
                      : !selectedCustomer && (newCustomer.address || newCustomer.address_code)
                        ? [newCustomer.address_code, newCustomer.address].filter(Boolean).join(" — ")
                        : <span className="omodal__sum-empty">— nevybraná —</span>}
                  </span>
                </div>
                <div className="omodal__location-row">
                  {deliveryLocation ? (
                    <>
                      <span className="omodal__location-tag"><MapPin size={14} /> Poloha spresnená</span>
                      <button type="button" className="omodal__location-action" onClick={() => setLocationPickerOpen(true)}>
                        Zmeniť
                      </button>
                      <button type="button" className="omodal__location-action omodal__location-action--remove" onClick={() => setDeliveryLocation(null)} aria-label="Zrušiť polohu">
                        <X size={14} />
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="omodal__location-button"
                      onClick={() => setLocationPickerOpen(true)}
                      disabled={!selectedAddressId && !hasNewCustomerAddressPair}
                    >
                      <MapPin size={15} /> Spresniť polohu na mape
                    </button>
                  )}
                </div>
                <div className="omodal__sum-row">
                  <span className="omodal__sum-row-label">Trasa</span>
                  <span className="omodal__sum-row-val omodal__sum-row-val--flex">
                    {selectedRoute
                      ? <><span className="omodal__route-dot" style={{ background: selectedRoute.color_code }} /> {selectedRoute.name}</>
                      : <span className="omodal__sum-empty">— bez trasy —</span>}
                  </span>
                </div>
                <div className="omodal__sum-divider" />
                <div className="omodal__sum-row">
                  <span className="omodal__sum-row-label">Medzisúčet</span>
                  <span className="omodal__sum-row-val omodal__sum-row-val--mono">{total.toFixed(2)} €</span>
                </div>
                <div className="omodal__sum-row">
                  <span className="omodal__sum-row-label">Starý dlh</span>
                  <span className={`omodal__sum-row-val omodal__sum-row-val--mono${selectedCustomer && (selectedCustomer.actual_saldo || 0) < 0 ? " omodal__sum-row-val--debt" : ""}`}>
                    {selectedCustomer ? Number(selectedCustomer.actual_saldo || 0).toFixed(2) : "0.00"} €
                  </span>
                </div>
                <div className="omodal__sum-total-row">
                  <span className="omodal__sum-total-label">Celkom na úhradu</span>
                  <span className="omodal__sum-total-val">
                    {(total + (selectedCustomer ? Math.max(0, -(selectedCustomer.actual_saldo || 0)) : 0)).toFixed(2)} €
                  </span>
                </div>
              </div>

              {error && (
                <div className="omodal__error">
                  <AlertTriangle size={14} /> {error}
                </div>
              )}

              <button
                className="omodal__submit"
                onClick={handleSubmit}
                disabled={!canSubmit}
              >
                {submitting ? (
                  <><Loader2 size={16} className="omodal__spin" /> Ukladám…</>
                ) : (editOrder || autoFilledOrderId) ? (
                  <><CheckCircle2 size={16} /> Uložiť zmeny</>
                ) : (
                  <><CheckCircle2 size={16} /> Zadať objednávku</>
                )}
              </button>
              </div>{/* end omodal__sum-footer */}
            </div>

          </div>
        </div>
      </div>
    </div>

    <Modal
      isOpen={debtConfirmOpen}
      onClose={() => setDebtConfirmOpen(false)}
      title="Objednávka na vyplatenie dlhu"
      className="omodal__debt-confirm"
      footer={
        <>
          <Button variant="outline" onClick={() => setDebtConfirmOpen(false)}>
            Nie
          </Button>
          <Button
            variant="primary"
            onClick={() => { setDebtConfirmOpen(false); submitOrder(); }}
          >
            Áno, vytvoriť
          </Button>
        </>
      }
    >
      <p>
        Tento zákazník nemá vybraté žiadne jedlá, ale má evidovaný dlh.
        Prajete si vytvoriť objednávku na vyplatenie dlhu?
      </p>
    </Modal>
    <Modal
      isOpen={sposobConfirmOpen}
      onClose={() => setSposobConfirmOpen(false)}
      title="Zmena spôsobu odberu"
      footer={
        <>
          <Button variant="outline" onClick={() => setSposobConfirmOpen(false)}>
            Nie
          </Button>
          <Button
            variant="primary"
            onClick={() => { setSposobConfirmOpen(false); submitOrder(); }}
          >
            Áno, zmeniť
          </Button>
        </>
      }
    >
      <p>
        {deliveryType === "DELIVERY"
          ? "Zákazník si objednal odber na prevádzke. Naozaj mu objednávku doveziete?"
          : "Zákazník si objednal donášku. Naozaj si príde na prevádzku sám?"}
      </p>
      <p>Zákazníkovi o zmene pošleme e-mail.</p>
    </Modal>
    <DeliveryLocationPicker
      isOpen={locationPickerOpen}
      address={selectedCustomer?.addresses?.find(a => a.id === selectedAddressId)?.address || newCustomer.address}
      location={deliveryLocation}
      onConfirm={nextLocation => setDeliveryLocation(nextLocation)}
      onClose={() => setLocationPickerOpen(false)}
    />
    </>
  );
}

export default OrderModal;
